/**
 * 公告通知类型配置文件 - 数据处理层
 *
 * 架构说明：
 * - 数据处理层（noticeConfig.js）：负责定义不同公告类型的数据源、接口映射、字段适配
 * - UI展示层（NoticeList.vue / notice/List.vue / notice/Detail.vue）：只接收标准化数据，通过 emit 通知父组件
 *
 * 新增公告类型时，只需在 NOTICE_TYPES 中添加新的配置项即可。
 *
 * @field {String}   type            - 类型唯一标识（URL query 参数值）
 * @field {String}   titleKey        - 国际化标题 key（列表/侧边栏标题）
 * @field {String}   listTitleKey    - 国际化列表页标题 key
 * @field {String}   listApiMethod   - 列表接口方法名（对应 $api 上的方法）
 * @field {String}   detailApiMethod - 详情接口方法名（对应 $api 上的方法）
 * @field {String}   listRoute       - 列表页路由 path
 * @field {String}   detailRoute     - 详情页路由 path
 * @field {String}   backRouteName   - 返回按钮跳转的路由 name（列表页返回首页）
 * @field {Function} normalizeList   - 将接口列表数据适配为统一的 NoticeItem 格式
 * @field {Function} normalizeDetail - 将接口详情数据适配为统一的 NoticeDetail 格式
 */

/**
 * 统一公告列表项格式
 * @typedef {Object} NoticeItem
 * @property {String} instId        - 公告唯一 ID
 * @property {String} title         - 公告标题
 * @property {String} createTime    - 创建时间
 */

/**
 * 统一公告详情格式
 * @typedef {Object} NoticeDetail
 * @property {String} instId          - 公告唯一 ID
 * @property {String} title           - 公告标题
 * @property {String} content         - 公告内容（HTML 格式）
 * @property {String} createTime      - 创建时间
 * @property {String|null} publishTime - 发布时间（可选）
 * @property {NoticeItem|null} prev   - 上一篇（null 表示没有）
 * @property {NoticeItem|null} next   - 下一篇（null 表示没有）
 */

export const NOTICE_TYPES = {
  /**
   * 普通公告（默认类型）
   */
  announcement: {
    type: 'announcement',
    titleKey: 'overview.notice.title',
    listTitleKey: 'overview.notice.listTitle',
    backListKey: 'overview.notice.backAnnouncementList',
    listApiMethod: 'getListNotices',
    detailApiMethod: 'getNoticeInfo',
    listRoute: '/notice/list',
    detailRoute: '/notice/detail',
    backRouteName: 'homeOverView',

    /**
     * 将接口返回的列表数据适配为统一格式
     * @param {Array} rawList - 接口返回的原始列表
     * @returns {NoticeItem[]}
     */
    normalizeList(rawList) {
      return (rawList || []).map(item => ({
        instId: item.instId,
        title: item.announcementTitle,
        createTime: item.createTime,
      }));
    },

    /**
     * 将接口返回的详情数据适配为统一格式
     * @param {Object} raw - 接口返回的原始详情对象
     * @returns {NoticeDetail}
     */
    normalizeDetail(raw) {
      const normalizeNavItem = item => item
        ? { instId: item.instId, title: item.announcementTitle }
        : null;
      return {
        instId: raw.instId,
        title: raw.announcementTitle,
        content: raw.announcementContent,
        createTime: raw.createTime,
        publishTime: raw.publishTime || null,
        prev: normalizeNavItem(raw.prevNotice),
        next: normalizeNavItem(raw.nextNotice),
      };
    },

    /**
     * 从接口响应中提取列表数组及分页信息
     * @param {Object} resData - res.data
     * @returns {{ list: Array, totalCount: Number }}
     */
    extractListData(resData) {
      return {
        list: resData.notices || [],
        totalCount: resData.totalCount || 0,
      };
    },

    /**
     * 从接口响应中提取详情对象
     * @param {Object} resData - res.data
     * @returns {Object}
     */
    extractDetailData(resData) {
      return resData.notice || {};
    },
  },

  /**
   * 封网和割接通知
   */
  networkFreezeCutover: {
    type: 'networkFreezeCutover',
    titleKey: 'overview.notice.networkFreezeCutoverTitle',
    listTitleKey: 'overview.notice.networkFreezeCutoverListTitle',
    backListKey: 'overview.notice.backNoticeList',
    listApiMethod: 'getNetworkFreezeCutoverList',
    detailApiMethod: 'getNetworkFreezeCutoverInfo',
    listRoute: '/networkFreezeCutover/list',
    detailRoute: '/networkFreezeCutover/detail',
    backRouteName: 'homeOverView',

    normalizeList(rawList) {
      return (rawList || []).map(item => ({
        instId: item.instId,
        title: item.noticeTitle,
        createTime: item.createTime,
        // 封网割接通知额外字段
        eventType: item.eventType,
        status: item.status,
      }));
    },

    normalizeDetail(raw) {
      const normalizeNavItem = item => item
        ? { instId: item.instId, title: item.noticeTitle }
        : null;
      return {
        instId: raw.instId,
        title: raw.noticeTitle,
        content: raw.noticeContent,
        createTime: raw.createTime,
        publishTime: null,
        eventType: raw.eventType,
        status: raw.status,
        prev: normalizeNavItem(raw.prevNotice),
        next: normalizeNavItem(raw.nextNotice),
      };
    },

    extractListData(resData) {
      return {
        list: resData.notices || [],
        totalCount: resData.totalCount || 0,
      };
    },

    extractDetailData(resData) {
      return resData.notice || {};
    },
  },
};

/**
 * 根据 type 字符串获取对应的通知配置
 * @param {String} type - 通知类型标识
 * @returns {Object} 对应的配置对象，未找到则返回默认的 announcement 配置
 */
export function getNoticeConfig(type) {
  return NOTICE_TYPES[type] || NOTICE_TYPES.announcement;
}

export default NOTICE_TYPES;



// WEBPACK FOOTER //
// ./src/components/notice/noticeConfig.js